import React, { Component } from "react";
import { Tag, Tooltip, Dropdown, Menu, Button, Modal, Input, Alert, Select, Spin, message, Empty } from "antd";
import { MoreOutlined, AlertOutlined, CalendarOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { cmmsPost, formatoFechaHora, estatusEquipo } from "../cmmsComun";
import "../cmms.css";

const { Option } = Select;

// Tablero de servicios: una columna por estatus. Arrastrar una tarjeta a otra columna cambia
// su estatus con las mismas reglas de siempre:
//  - Programado <-> En proceso: directo.
//  - -> Concluido: corre "concluir" (descuenta refacciones, actualiza el equipo, el plan y el
//    ticket). Si le falta algo (ingeniero, condicion de entrega) ofrece abrir el servicio.
//  - -> Cancelado ("quitar"): pide el motivo; un concluido regresa sus refacciones.
//  - Un cancelado ya no se mueve. Un concluido solo puede cancelarse.

const COLUMNAS = {
    1: { titulo: "Programado", hex: "#7a8794" },
    2: { titulo: "En proceso", hex: "#2fb8d6" },
    3: { titulo: "Concluido", hex: "#2e9e6b" },
    0: { titulo: "Cancelado", hex: "#d64545" },
};

const permitido = (de, a) => {
    if (de === a) return false;
    if (de === 0) return false;
    if (de === 3) return a === 0;
    return true;
};

class TableroServicios extends Component {
    state = { cargando: false, columnas: [], arrastrando: null, sobre: null, cancelar: null, diasCerrados: 30, guardando: false };

    componentDidMount() {
        this.consultar();
    }

    componentDidUpdate(prev) {
        if (JSON.stringify(prev.filtros) !== JSON.stringify(this.props.filtros) || prev.recarga !== this.props.recarga) this.consultar();
    }

    consultar = async () => {
        this.setState({ cargando: true });
        try {
            const res = await cmmsPost(this.props, "tableroServiciosCMMS", { ...this.props.filtros, diasCerrados: this.state.diasCerrados });
            if (res.data.success === 2) this.setState({ columnas: res.data.columnas });
            else message.error(res.data.message || "No se pudo consultar el tablero");
        } catch (error) {
            console.log(error);
            message.error("No se pudo consultar el tablero");
        } finally {
            this.setState({ cargando: false });
        }
    };

    abrir = (id, paso) => this.props.history.push("/panel/cmmsServicio", { id, ...(paso !== undefined ? { paso } : {}) });

    mover = async (s, destino) => {
        if (this.state.guardando) return; // un movimiento a la vez
        if (!permitido(s.estatus, destino)) {
            if (s.estatus !== destino) message.info(s.estatus === 0 ? "Un servicio cancelado ya no cambia de estatus" : "Un servicio concluido sólo se puede cancelar");
            return;
        }
        if (destino === 0) return this.setState({ cancelar: { servicio: s, motivo: "" } });
        if (destino === 3) {
            Modal.confirm({
                title: `¿Concluir ${s.codigo}?`,
                content: "Se descuentan las refacciones marcadas, el equipo toma la condición de entrega como su estatus y se recalcula su próximo preventivo.",
                okText: "Concluir",
                cancelText: "Cancelar",
                onOk: () => this.concluir(s),
            });
            return;
        }
        this.setState({ guardando: true });
        try {
            const res = await cmmsPost(this.props, "cambiarEstatusServicioCMMS", { id: s._id, estatus: destino });
            if (res.data.success === 2) message.success(`${s.codigo}: ${COLUMNAS[destino].titulo}`);
            else message.error(res.data.message || "No se pudo cambiar el estatus");
        } catch (error) {
            console.log(error);
            message.error("No se pudo cambiar el estatus");
        } finally {
            this.setState({ guardando: false }, this.consultar);
        }
    };

    concluir = async (s) => {
        let res;
        this.setState({ guardando: true });
        try {
            res = await cmmsPost(this.props, "concluirServicioCMMS", { id: s._id });
        } catch (error) {
            console.log(error);
            return message.error("No se pudo concluir el servicio");
        } finally {
            this.setState({ guardando: false });
        }
        if (res.data.success === 2) {
            message.success(`${s.codigo} concluido`);
            this.consultar();
            return;
        }
        if (res.data.success !== 1) return message.error("Error del servidor al concluir el servicio");
        // Falta capturar algo: se ofrece abrir el servicio en el paso donde va.
        const paso = /ingeniero/i.test(res.data.message || "") ? 0 : /condición/i.test(res.data.message || "") ? 1 : undefined;
        Modal.confirm({
            title: "No se pudo concluir",
            icon: <ExclamationCircleOutlined />,
            content: `${res.data.message || "Faltan datos"}. ¿Abrir el servicio para completarlo?`,
            okText: "Abrir servicio",
            cancelText: "Cerrar",
            onOk: () => this.abrir(s._id, paso),
        });
    };

    cancelar = async () => {
        const { servicio, motivo } = this.state.cancelar;
        if (!motivo.trim()) return message.error("Escriba el motivo");
        this.setState({ guardando: true });
        try {
            const res = await cmmsPost(this.props, "cancelarServicioCMMS", { id: servicio._id, motivo });
            if (res.data.success !== 2) return message.error(res.data.message || "No se pudo cancelar");
            message.success(`${servicio.codigo} cancelado`);
            this.setState({ cancelar: null }, this.consultar);
        } catch (error) {
            console.log(error);
            message.error("No se pudo cancelar el servicio");
        } finally {
            this.setState({ guardando: false });
        }
    };

    menu = (s) => (
        <Menu
            onClick={({ key, domEvent }) => {
                domEvent.stopPropagation();
                if (key === "abrir") this.abrir(s._id);
                else this.mover(s, Number(key));
            }}
        >
            <Menu.Item key="abrir">Abrir</Menu.Item>
            {[1, 2, 3]
                .filter((e) => permitido(s.estatus, e))
                .map((e) => (
                    <Menu.Item key={String(e)}>Pasar a {COLUMNAS[e].titulo}</Menu.Item>
                ))}
            {permitido(s.estatus, 0) && (
                <Menu.Item key="0" danger>
                    Cancelar (quitar)
                </Menu.Item>
            )}
        </Menu>
    );

    tarjeta = (s) => (
        <div
            key={s._id}
            className={`cmms-tarjeta ${this.state.arrastrando?._id === s._id ? "arrastrando" : ""}`}
            draggable={s.estatus !== 0}
            onDragStart={(e) => {
                e.dataTransfer.effectAllowed = "move";
                // Firefox no inicia el arrastre si no se le pone algun dato.
                e.dataTransfer.setData("text/plain", String(s._id));
                this.setState({ arrastrando: s });
            }}
            onDragEnd={() => this.setState({ arrastrando: null, sobre: null })}
            onDoubleClick={() => this.abrir(s._id)}
        >
            <div className="cmms-tarjeta-cab">
                <b>{s.codigo}</b>
                {s.folioExterno ? <span className="cmms-tarjeta-sub">{s.folioExterno}</span> : null}
                <span style={{ marginLeft: "auto", display: "inline-flex", gap: 4, alignItems: "center" }}>
                    {s.ticket ? (
                        <Tooltip title="Viene de un ticket">
                            <AlertOutlined style={{ color: "#d9962b" }} />
                        </Tooltip>
                    ) : null}
                    {s.programa ? (
                        <Tooltip title="Viene del programa del contrato">
                            <CalendarOutlined style={{ color: "#2f80ed" }} />
                        </Tooltip>
                    ) : null}
                    <Dropdown overlay={this.menu(s)} trigger={["click"]}>
                        <Button type="text" size="small" icon={<MoreOutlined />} onClick={(e) => e.stopPropagation()} />
                    </Dropdown>
                </span>
            </div>
            <div className="cmms-tarjeta-equipo">{s.activoTexto}</div>
            <div className="cmms-tarjeta-sub">{[s.clienteNombre, s.contratoNombre].filter(Boolean).join(" · ")}</div>
            <div className="cmms-tarjeta-pie">
                <Tag style={{ marginRight: 0 }}>{s.tipoServicioNombre}</Tag>
                {s.entrega ? <Tag color={estatusEquipo(s.entrega).color}>{estatusEquipo(s.entrega).label}</Tag> : null}
                {s.terminadoEnSitio ? (
                    <Tooltip title="El ingeniero la terminó en sitio desde la app: revísela y concluya">
                        <Tag color="green">Terminada en sitio</Tag>
                    </Tooltip>
                ) : null}
                <span className="cmms-tarjeta-sub" style={{ marginLeft: "auto" }}>
                    {s.empleadoNombre || "Sin ingeniero"}
                </span>
            </div>
            {s.fecha ? <div className="cmms-tarjeta-sub">{formatoFechaHora(s.fecha)}</div> : null}
            {s.estatus === 0 && s.motivoCancelacion ? <div className="cmms-tarjeta-sub">Motivo: {s.motivoCancelacion}</div> : null}
        </div>
    );

    render() {
        const { arrastrando, sobre } = this.state;
        return (
            <Spin spinning={this.state.cargando}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
                    <span className="cmms-ayuda" style={{ margin: 0 }}>
                        Arrastre una tarjeta a otra columna para cambiar su estatus, o use el menú ⋯ de la tarjeta. Doble clic la abre.
                    </span>
                    <span style={{ fontSize: 12 }}>
                        Concluidos y cancelados de los últimos{" "}
                        <Select size="small" value={this.state.diasCerrados} onChange={(diasCerrados) => this.setState({ diasCerrados }, this.consultar)} style={{ width: 90 }}>
                            {[7, 15, 30, 60, 90].map((d) => (
                                <Option key={d} value={d}>
                                    {d} días
                                </Option>
                            ))}
                        </Select>
                    </span>
                </div>
                <div className="cmms-tablero">
                    {this.state.columnas.map((c) => {
                        const col = COLUMNAS[c.estatus];
                        const aceptaDrop = arrastrando && permitido(arrastrando.estatus, c.estatus);
                        return (
                            <div
                                key={c.estatus}
                                className={`cmms-columna ${aceptaDrop ? "acepta" : ""} ${sobre === c.estatus && aceptaDrop ? "sobre" : ""}`}
                                onDragOver={(e) => {
                                    if (!aceptaDrop) return;
                                    e.preventDefault();
                                    if (sobre !== c.estatus) this.setState({ sobre: c.estatus });
                                }}
                                onDragLeave={() => sobre === c.estatus && this.setState({ sobre: null })}
                                onDrop={(e) => {
                                    e.preventDefault();
                                    const s = this.state.arrastrando;
                                    this.setState({ arrastrando: null, sobre: null });
                                    if (s) this.mover(s, c.estatus);
                                }}
                            >
                                <div className="cmms-columna-cab" style={{ borderTopColor: col.hex }}>
                                    <span className="cmms-cal-chip-punto" style={{ background: col.hex }} />
                                    <b>{col.titulo}</b>
                                    <span className="cmms-columna-total">{c.total}</span>
                                </div>
                                <div className="cmms-columna-cuerpo">
                                    {c.servicios.length ? c.servicios.map(this.tarjeta) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Sin servicios" />}
                                    {c.total > c.servicios.length ? <div className="cmms-ayuda" style={{ textAlign: "center" }}>Se muestran {c.servicios.length} de {c.total}. Use los filtros o la tabla.</div> : null}
                                </div>
                            </div>
                        );
                    })}
                </div>
                <Modal
                    visible={!!this.state.cancelar}
                    title={`Cancelar ${this.state.cancelar?.servicio?.codigo || ""}`}
                    onCancel={() => this.setState({ cancelar: null })}
                    onOk={this.cancelar}
                    confirmLoading={this.state.guardando}
                    okText="Cancelar servicio"
                    cancelText="Regresar"
                    okButtonProps={{ danger: true }}
                >
                    {this.state.cancelar?.servicio?.estatus === 3 && (
                        <Alert
                            type="warning"
                            showIcon
                            style={{ marginBottom: 10 }}
                            message="El servicio está concluido: las refacciones que se descontaron regresan al almacén y las fechas del equipo se recalculan."
                        />
                    )}
                    <div className="cmms-ayuda" style={{ margin: "0 0 8px" }}>
                        El folio no se reutiliza. Si venía del programa del contrato, el equipo vuelve a la agenda.
                    </div>
                    <Input.TextArea
                        rows={3}
                        placeholder="Motivo de la cancelación (obligatorio)"
                        value={this.state.cancelar?.motivo || ""}
                        onChange={(e) => this.setState({ cancelar: { ...this.state.cancelar, motivo: e.target.value } })}
                    />
                </Modal>
            </Spin>
        );
    }
}

export default TableroServicios;
